import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, BookText, CalendarClock, ExternalLink, Undo2 } from "lucide-react";
import { EmptyState, ErrorState, HeaderRow, LoadingState, Pager, rowLink, TableShell, TH } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import {
  AccountSelect,
  BooksPeriodScreen,
  EVENT_LABEL,
  money,
  moneyOrBlank,
  num,
  SOURCE_LABEL,
  bankLineHref,
  drCr,
  sourceEventLabel,
  sourceHref,
  useChart,
} from "./shared";
import type { JournalEntry, JournalSourceType } from "./types";
import { usePeriodLink, type Period } from "./period";

const ALL = "__all__";

export function GeneralLedger() {
  return (
    <BooksPeriodScreen basis title="General ledger" subtitle="Every journal entry. Entries are never edited: corrections are reversals.">
      {/* Keyed by the period, so a new period starts again from its first page. */}
      {(_book, period) => <LedgerBody key={`${period.query}&${period.basis}`} period={period} />}
    </BooksPeriodScreen>
  );
}

function LedgerBody({ period }: { period: Period }) {
  const { books } = useBooks();
  const chart = useChart();
  const [params, setParams] = useSearchParams();
  const accountId = params.get("accountId");
  const sourceType = params.get("sourceType") as JournalSourceType | null;
  const openEntry = params.get("entry");
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const cursor = cursors[cursors.length - 1];

  const set = (key: string, value: string | null) => {
    setCursors([undefined]);
    setParams(
      (p) => {
        if (value) p.set(key, value);
        else p.delete(key);
        return p;
      },
      { replace: key !== "entry" },
    );
  };

  const page = useLoad(
    () =>
      books.GET("/v1/books/current/journal-entries", {
        params: {
          query: {
            AccountId: accountId ?? undefined,
            From: period.fromDate,
            To: period.toDate,
            SourceType: sourceType ?? undefined,
            Cursor: cursor,
            Limit: 50,
            Basis: period.basis,
          },
        },
      }),
    [books, accountId, period.fromDate, period.toDate, sourceType, cursor, period.basis],
  );
  const entries = page.data?.data ?? [];
  const account = (chart.data ?? []).find((a) => a.id === accountId);
  // Only one account with no source filter has a balance to run; the server leaves these null otherwise.
  const closing = page.data?.closingBalance;
  const balances = accountId !== null && closing != null;
  const firstPage = cursors.length === 1;
  const lastPage = !page.data?.meta?.cursors?.next;

  return (
    <>
      <div className="mb-4 grid gap-3 @md:grid-cols-2">
        <AccountSelect
          id="gl-account"
          label="Account"
          optional
          noneLabel="All accounts"
          value={accountId}
          accounts={chart.data ?? []}
          onChange={(id) => set("accountId", id)}
        />
        <div className="space-y-1.5">
          <Label htmlFor="gl-source">Source</Label>
          <Select value={sourceType ?? ALL} onValueChange={(v) => set("sourceType", v === ALL ? null : v)}>
            <SelectTrigger id="gl-source" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any source</SelectItem>
              {Object.entries(SOURCE_LABEL).map(([k, label]) => (
                <SelectItem key={k} value={k}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {period.basis === "Cash" ? (
        <p className="mb-3 text-sm text-muted-foreground">
          Cash basis: each payment shows the income, expenses and VAT it released from its invoice or bill. Unpaid invoices and
          bills are not listed.
        </p>
      ) : null}
      {page.loading && !page.data ? (
        <LoadingState label="Loading entries" />
      ) : page.error ? (
        <ErrorState message={apiErrorMessage(page.error, "Couldn't load the entries.")} onRetry={page.reload} />
      ) : entries.length === 0 ? (
        <EmptyState icon={BookText} title="No entries">
          {accountId || sourceType ? "Nothing matches these filters in this period." : `No entries in ${period.label}.`}
          {balances
            ? num(page.data?.openingBalance) === num(closing)
              ? ` The balance is ${money(closing)} throughout.`
              : ` The balance is ${money(page.data?.openingBalance)} at the start and ${money(closing)} at the end.`
            : null}
        </EmptyState>
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Date</TableHead>
                  <TableHead className={TH}>Description</TableHead>
                  <TableHead className={cn(TH, "hidden @3xl:table-cell")}>Source</TableHead>
                  {accountId ? (
                    <>
                      <TableHead className={cn(TH, "text-right", balances && WIDE)}>Debit</TableHead>
                      <TableHead className={cn(TH, "text-right", balances && WIDE)}>Credit</TableHead>
                      {balances ? <TableHead className={cn(TH, "text-right", NARROW)}>Amount</TableHead> : null}
                    </>
                  ) : (
                    <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                  )}
                  {balances ? <TableHead className={cn(TH, "text-right")}>Balance</TableHead> : null}
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {balances && firstPage ? <BalanceRow label="Closing balance" date={period.toDate} amount={closing} /> : null}
                {entries.map((e) => {
                  const { debit, credit } = amounts(e, accountId);
                  return (
                    <TableRow key={e.id} {...rowLink(() => set("entry", e.id!))}>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatCalendarDate(e.entryDate ?? "")}
                        {e.originalDate ? (
                          <div className="text-xs text-muted-foreground">from {formatCalendarDate(e.originalDate)}</div>
                        ) : null}
                      </TableCell>
                      {/* Takes the slack and truncates; in a narrow pane (chat open) the source folds under it. */}
                      <TableCell className="w-full max-w-0">
                        <div className="truncate">{e.description || "-"}</div>
                        <div className="truncate text-xs text-muted-foreground @3xl:hidden">
                          {sourceEventLabel(e.sourceType, e.sourceEvent)}
                        </div>
                        {e.released ? (
                          <div className="text-xs font-medium text-sky-700 dark:text-sky-400">{releasedLabel(e)}</div>
                        ) : e.reversesEntryId ? (
                          <div className="text-xs text-muted-foreground">Reversal</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden whitespace-nowrap text-muted-foreground @3xl:table-cell">
                        {sourceEventLabel(e.sourceType, e.sourceEvent)}
                      </TableCell>
                      {accountId ? (
                        <>
                          <TableCell className={cn("text-right tabular-nums", balances && WIDE)}>{moneyOrBlank(debit)}</TableCell>
                          <TableCell className={cn("text-right tabular-nums", balances && WIDE)}>{moneyOrBlank(credit)}</TableCell>
                          {balances ? (
                            <TableCell className={cn("whitespace-nowrap text-right tabular-nums", NARROW)}>{drCr(debit, credit)}</TableCell>
                          ) : null}
                        </>
                      ) : (
                        <TableCell className="text-right tabular-nums">{money(debit)}</TableCell>
                      )}
                      {balances ? <TableCell className="whitespace-nowrap text-right tabular-nums">{money(e.runningBalance)}</TableCell> : null}
                    </TableRow>
                  );
                })}
                {balances && lastPage ? (
                  <BalanceRow label="Opening balance" date={period.fromDate} amount={page.data?.openingBalance} />
                ) : null}
              </TableBody>
            </Table>
          </TableShell>
          {account ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Debits and credits on {account.name} only.
              {balances ? " Balances read as on the trial balance, credit positive for liabilities, equity and income." : null}
            </p>
          ) : null}
          <Pager
            page={cursors.length}
            hasPrevious={cursors.length > 1}
            hasNext={Boolean(page.data?.meta?.cursors?.next)}
            disabled={page.loading}
            onPrevious={() => setCursors((c) => c.slice(0, -1))}
            onNext={() => setCursors((c) => [...c, page.data?.meta?.cursors?.next ?? undefined])}
          />
        </>
      )}
      <EntryDrawer
        entryId={openEntry}
        released={entries.find((e) => e.id === openEntry && e.released)}
        onOpen={(id) => set("entry", id)}
        onClose={() => set("entry", null)}
      />
    </>
  );
}

/** In a narrow pane (the agent open) debit and credit fold into one column, so the balance still fits. */
const WIDE = "hidden @xl:table-cell";
const NARROW = "@xl:hidden";

/** The account's balance at one end of the range, newest first: closing above the entries, opening below them. */
function BalanceRow({ label, date, amount }: { label: string; date?: string; amount: number | string | null | undefined }) {
  return (
    <TableRow className="bg-muted/40 font-medium hover:bg-muted/40">
      <TableCell className="whitespace-nowrap tabular-nums">{date ? formatCalendarDate(date) : null}</TableCell>
      <TableCell>{label}</TableCell>
      <TableCell className="hidden @3xl:table-cell" />
      <TableCell className={WIDE} />
      <TableCell className={WIDE} />
      <TableCell className={NARROW} />
      <TableCell className="whitespace-nowrap text-right tabular-nums">{money(amount)}</TableCell>
    </TableRow>
  );
}

/** What a cash-basis row's lines are: a payment's share of its document, or what a change to the document released. */
const docLabel = (e: JournalEntry) => (e.documentType ? SOURCE_LABEL[e.documentType].toLowerCase() : "document");

function releasedLabel(e: JournalEntry) {
  const doc = docLabel(e);
  if (e.reversesEntryId) return `Cash basis: takes back what the reversed entry released`;
  return e.sourceEvent === "InvoicePaid" || e.sourceEvent === "BillPaid"
    ? `Cash basis: released by this payment of the ${doc}`
    : `Cash basis: released by this change to the ${doc}`;
}

/** Filtered to an account, a row shows that account's side; otherwise `debit` is the entry's total. */
function amounts(e: JournalEntry, accountId: string | null) {
  const lines = (e.lines ?? []).filter((l) => !accountId || l.ledgerAccountId === accountId);
  return {
    debit: lines.reduce((s, l) => s + num(l.debit), 0),
    credit: lines.reduce((s, l) => s + num(l.credit), 0),
  };
}

export function EntryDrawer({
  entryId,
  released,
  onOpen,
  onClose,
}: {
  entryId: string | null;
  /** The entry as a cash-basis listing showed it, when its lines were what it released. */
  released?: JournalEntry;
  onOpen: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <Sheet open={entryId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-xl">
        {entryId ? <EntryDetail entryId={entryId} released={released} onOpen={onOpen} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function EntryDetail({ entryId, released, onOpen }: { entryId: string; released?: JournalEntry; onOpen: (id: string) => void }) {
  const { books, links } = useBooks();
  const entry = useLoad(
    () => books.GET("/v1/books/current/journal-entries/{id}", { params: { path: { id: entryId } } }),
    [books, entryId],
  );
  const e = entry.data;
  // The page-bearing document (an invoice payment opens its invoice); a payout has none, so its matched bank transaction.
  const withPeriod = usePeriodLink();
  const page = e ? sourceHref(links, e.documentType ?? undefined, e.documentId ?? undefined) : null;
  // Books pages keep the period; the host's document pages have none.
  const href = page?.startsWith("/books") ? withPeriod(page) : page;
  const bankLine =
    e?.matchedBankLineId && e.documentType !== "BankLine" ? withPeriod(bankLineHref(e.matchedBankLineId, "Matched")) : null;

  return (
    <>
      <SheetHeader className="border-b">
        <SheetTitle>{e?.description || "Journal entry"}</SheetTitle>
        <SheetDescription>
          {e ? `${sourceEventLabel(e.sourceType, e.sourceEvent)}${num(e.revision) > 0 ? ` · revision ${e.revision}` : ""}` : " "}
        </SheetDescription>
      </SheetHeader>
      <div className="space-y-5 p-4">
        {entry.loading && !e ? (
          <LoadingState label="Loading the entry" />
        ) : entry.error ? (
          <ErrorState message={apiErrorMessage(entry.error, "Couldn't load the entry.")} onRetry={entry.reload} />
        ) : !e ? null : (
          <>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Date</dt>
                <dd className="font-medium">{formatCalendarDate(e.entryDate ?? "")}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Posted</dt>
                <dd>{formatDateTime(e.postedAt)}</dd>
              </div>
            </dl>
            {e.originalDate ? (
              <p className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-xs">
                <CalendarClock className="mt-px size-3.5 shrink-0" />
                Dated {formatCalendarDate(e.originalDate)}, which was on or before the close date, so it posted on the first
                open day.
              </p>
            ) : null}

            {href || bankLine ? (
              <div className="flex flex-wrap gap-2">
                {href ? (
                  <Button asChild variant="outline" size="sm">
                    <Link to={href}>
                      Open the {SOURCE_LABEL[e.documentType!].toLowerCase()} <ExternalLink className="size-3.5" />
                    </Link>
                  </Button>
                ) : null}
                {bankLine ? (
                  <Button asChild variant="outline" size="sm">
                    <Link to={bankLine}>
                      Open the matched bank transaction <ExternalLink className="size-3.5" />
                    </Link>
                  </Button>
                ) : null}
              </div>
            ) : null}

            {released ? (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">On cash basis</h3>
                <p className="text-xs text-muted-foreground">
                  {released.sourceEvent === "InvoicePaid" || released.sourceEvent === "BillPaid"
                    ? `This payment counts its share of the ${docLabel(released)}'s ${released.sourceEvent === "BillPaid" ? "costs" : "income"} and VAT, in proportion to the amount paid.`
                    : `This change to the ${docLabel(released)} moves what its payments had counted so far.`}
                </p>
                <LinesTable lines={released.lines ?? []} />
              </section>
            ) : null}

            <section className="space-y-2">
              {released ? <h3 className="text-sm font-semibold">As posted</h3> : null}
              <LinesTable lines={e.lines ?? []} />
            </section>

            {(e.sourceEntries ?? []).length > 1 ? (
              <section className="space-y-2">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold">
                  <Undo2 className="size-4 text-muted-foreground" /> History of this {SOURCE_LABEL[e.sourceType!].toLowerCase()}
                </h3>
                <ol className="space-y-1">
                  {(e.sourceEntries ?? []).map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => onOpen(s.id!)}
                        disabled={s.id === e.id}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm",
                          s.id === e.id ? "bg-muted/50 font-medium" : "hover:bg-muted/40",
                        )}
                      >
                        <span>
                          {formatCalendarDate(s.entryDate ?? "")} · {EVENT_LABEL[s.sourceEvent!]}
                          {s.reversesEntryId ? " (reversal)" : ""}
                          {s.id === e.reversedByEntryId ? " · reverses this one" : ""}
                        </span>
                        {s.id === e.id ? (
                          <span className="text-xs text-muted-foreground">This entry</span>
                        ) : (
                          <ArrowRight className="size-3.5 text-muted-foreground" />
                        )}
                      </button>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

export function LinesTable({ lines }: { lines: NonNullable<JournalEntry["lines"]> }) {
  const totalDebit = lines.reduce((s, l) => s + num(l.debit), 0);
  const totalCredit = lines.reduce((s, l) => s + num(l.credit), 0);
  return (
    <TableShell>
      <Table>
        <TableHeader>
          <HeaderRow>
            <TableHead className={TH}>Account</TableHead>
            <TableHead className={cn(TH, "text-right")}>Debit</TableHead>
            <TableHead className={cn(TH, "text-right")}>Credit</TableHead>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {lines.map((l) => (
            <TableRow key={l.lineNumber}>
              <TableCell>
                <div className="font-medium">{l.accountCode ? `${l.accountCode} · ${l.accountName}` : l.accountName}</div>
                {l.description ? <div className="text-xs text-muted-foreground">{l.description}</div> : null}
              </TableCell>
              <TableCell className="text-right tabular-nums">{moneyOrBlank(l.debit)}</TableCell>
              <TableCell className="text-right tabular-nums">{moneyOrBlank(l.credit)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className="font-semibold">Total</TableCell>
            <TableCell className="text-right font-semibold tabular-nums">{money(totalDebit)}</TableCell>
            <TableCell className="text-right font-semibold tabular-nums">{money(totalCredit)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableShell>
  );
}
